import { computed, inject, Injectable, signal } from '@angular/core';
import {
  AuthorizeContext,
  AuthorizeRequest,
  AuthorizeResponse,
  isMfaRequired,
  LoginResponse,
  LoginRequest,
  SuccessLoginResponse,
  SessionTokenPayload,
} from '../../../shared/interfaces';
import { MfaService } from './mfa.service';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { SnackBarService } from '../utils/snack-bar.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // --------------- Propiedades --------------- //
  private readonly apiUrl = environment.apiUrl;

  // Signals de estado de sesión
  private _sessionToken = signal<string | null>(null);
  private _subtitleOverride = signal<string | null>(null);

  /** JWT de sesión (inicial desde authorize, o actualizado desde login con MFA) */
  sessionToken = this._sessionToken.asReadonly();

  /** Override del subtítulo de la página */
  subtitleOverride = this._subtitleOverride.asReadonly();

  /** Contexto de autorizacion (Parametros de autorizacion validados + redirectUri) */
  sessionContext = computed(() => {
    const payload = this.decodeSessionToken(this._sessionToken());
    if (!payload) return null;

    return {
      clientId: payload.clientId,
      redirectUri: payload.redirectUri,
      authTokenId: payload.authTokenId,
      responseType: payload.responseType,
      state: payload.state,
      scope: payload.scope,
    } as AuthorizeContext;
  });

  setSessionToken(value: string | null) {
    this._sessionToken.set(value);
  }

  setSubtitleOverride(value: string | null) {
    this._subtitleOverride.set(value);
  }

  /** Limpia toda la sesión (usado cuando expira o hay error crítico) y recarga la página */
  clearSession() {
    this._sessionToken.set(null);
    this._subtitleOverride.set(null);
    this.mfaService.clearMfaState();
    window.location.reload();
  }

  // --------------- Inyecciones --------------- //
  private readonly http = inject(HttpClient);
  private readonly snackbarService = inject(SnackBarService);
  private readonly mfaService = inject(MfaService);

  // --------------- Metodos --------------- //

  /**
   * Decodifica el payload del sessionToken JWT (sin validar firma)
   * @param token - JWT sessionToken
   * @returns SessionTokenPayload decodificado o null si es invalido
   */
  decodeSessionToken(token: string | null): SessionTokenPayload | null {
    if (!token) {
      return null;
    }

    try {
      // JWT tiene formato: header.payload.signature
      const parts = token.split('.');

      if (parts.length !== 3) {
        return null;
      }

      // Decodificar el payload (segunda parte)
      const payload = parts[1];

      // Reemplazar caracteres URL-safe de base64
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

      // Decodificar base64 a string
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );

      return JSON.parse(jsonPayload) as SessionTokenPayload;
    } catch {
      return null;
    }
  }

  /**
   * Obtiene el payload del sessionToken actual
   * @returns SessionTokenPayload decodificado o null
   */
  getSessionTokenPayload(): SessionTokenPayload | null {
    return this.decodeSessionToken(this.sessionToken());
  }

  /**
   * Completa el inicio de sesion y redirige al usuario
   * Construye la URL de redirección con code y parámetros OAuth opcionales
   * @param response - (type: SuccessLoginResponse) - respuesta del BFF con el code
   */
  completeLogin(response: SuccessLoginResponse) {
    const context = this.sessionContext();
    if (!context?.redirectUri) {
      return;
    }

    this.setSessionToken(null);
    this.mfaService.clearMfaState();
    this.snackbarService.open('Inicio de sesión exitoso. Redirigiendo...', {
      type: 'success',
      icon: 'check_circle',
    });

    // Construye URL con parámetros OAuth desde el sessionContext
    const params = new URLSearchParams();
    params.set('code', response.code);

    // Agrega los parametros opcionales existentes en la sesion
    if (context.state) params.set('state', context.state);
    if (context.scope) params.set('scope', context.scope);
    if (context.responseType) params.set('response_type', context.responseType);

    const redirectUrl = `${context.redirectUri}?${params.toString()}`;

    // Previene que la página quede en bfcache del navegador
    window.addEventListener('unload', () => { /* no-cache */ });

    window.location.href = redirectUrl;
  }

  // --------------- Llamadas a API --------------- //

  /**
   * Valida los parametros de autorizacion con el backend.
   * @param request - (type: AuthorizeRequest)
   * @returns Observable - ({@link AuthorizeContext})
   */
  validateAuthorize(request: AuthorizeRequest): Observable<AuthorizeContext> {
    return this.http.post<AuthorizeResponse>(`${this.apiUrl}/auth/authorize`, request).pipe(
      tap((response: AuthorizeResponse) => {
        this.setSessionToken(response.sessionToken);
      }),
      map((response: AuthorizeResponse): AuthorizeContext => {
        const payload = this.decodeSessionToken(response.sessionToken);

        return {
          clientId: payload?.clientId || request.clientId,
          redirectUri: payload?.redirectUri || request.redirectUri,
          authTokenId: payload?.authTokenId || undefined,
        };
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(() => err);
      }),
    );
  }

  /**
   * Envía las credenciales de inicio de sesión al servidor.
   * @param data - (type: LoginRequest)
   * @returns Observable - (type: LoginResponse) con SuccessLoginResponse (login directo) o MfaLoginResponse (requiere MFA)
   */
  submitLogin(data: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, data).pipe(
      tap((response: LoginResponse) => {
        if (isMfaRequired(response)) {
          // Actualizar sessionToken y canales disponibles
          this.setSessionToken(response.sessionToken);
          this.mfaService.setAvailableChannels(response.channels);
        }
      }),
      catchError((err) => {
        return throwError(() => err.error);
      }),
    );
  }

}
